/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  Atom,
  Blend,
  BookOpen,
  Boxes,
  ChartColumn,
  HardDrive,
  Lock,
  PackagePlus,
  TrendingUp,
  Truck,
  Waypoints,
} from "lucide-react";
import { type FC } from "react";
import { useLocation, useNavigate } from "react-router";
import { borders } from "../../styles";
import { theme } from "../../styles/theme/theme";

const NUCLEUS_COLOR = theme.colors.nucleus;
const BRAND_COLOR = theme.colors.blue600;

type IconComponent = FC<{ size?: number; strokeWidth?: number }>;

interface NavItem {
  href: string;
  name: string;
  icon: IconComponent;
  isNucleus?: boolean;
  primary?: boolean;
  /** Render as a non-navigable, locked-out item. Click is a no-op; a Lock
   *  icon is overlaid in the corner and the whole button is muted out. Used
   *  for any module that isn't available yet — the primary supply-chain tiles
   *  (Fill + Kit, Blend, Materials) as well as secondary tiles (Docs, Nucleus).
   *  The `href` is still required so the rest of the render path is uniform;
   *  it's just never navigated to. */
  locked?: boolean;
  /** Render in its normal style but dimmed and non-clickable — shown but turned
   *  off, with no "coming soon" lock badge. Currently unused (Docs/Nucleus moved
   *  to `locked`), kept for tiles that should read as off without a lock. */
  disabled?: boolean;
}

const primaryItems: NavItem[] = [
  { href: "/demand", name: "Demand", icon: TrendingUp as IconComponent, primary: true },
  { href: "/replenishment", name: "Replenishment", icon: Truck as IconComponent, primary: true, locked: true },
  { href: "/fill-kit", name: "Fill + Kit", icon: PackagePlus as IconComponent, primary: true, locked: true },
  { href: "/blend", name: "Blend", icon: Blend as IconComponent, primary: true, locked: true },
  { href: "/materials", name: "Materials", icon: Boxes as IconComponent, primary: true, locked: true },
];

const secondaryItems: NavItem[] = [
  { href: "/data", name: "Data", icon: HardDrive as IconComponent },
  { href: "/reporting", name: "Reporting", icon: ChartColumn as IconComponent },
  { href: "/digital-twin", name: "Digital Twin", icon: Waypoints as IconComponent },
  { href: "/docs", name: "Docs", icon: BookOpen as IconComponent, locked: true },
  { href: "/nucleus", name: "Nucleus", icon: Atom as IconComponent, isNucleus: true, locked: true },
];

const SIDEBAR_W = 96;
const { colors } = theme;

const s = {
  container: css`
    width: ${SIDEBAR_W}px;
    height: 100%;
    display: flex;
    flex-direction: column;
    ${borders.right}
    background-color: ${colors.background};
    position: fixed;
    z-index: 10;
  `,
  logoBar: css`
    display: flex;
    align-items: center;
    justify-content: center;
    ${borders.bottom}
    height: 3rem;
    box-sizing: border-box;
    flex-shrink: 0;
  `,
  logoImg: css`
    max-width: 80%;
    max-height: 80%;
    object-fit: contain;
    /* Dark artwork on a transparent PNG — dark mode floats it on a white
       chip (--color-logo-chip is transparent in light mode) */
    background-color: var(--color-logo-chip);
    border-radius: 4px;
    padding: 2px 4px;
    box-sizing: border-box;
  `,
  nav: css`
    display: flex;
    flex: 1;
    min-height: 0;
    flex-direction: column;
    align-items: center;
  `,
  // Primary modules scroll independently — this is the only shrinkable region,
  // so a short viewport scrolls here instead of pushing the secondary section
  // off-screen. It does NOT grow: with spare room the secondary section (and
  // its divider) floats up to sit directly under the last primary tile.
  primarySection: css`
    flex: 0 1 auto;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: thin;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    /* Horizontal inset so the near-full-width hover/active tiles keep a small
       breathing gap from the sidebar edges. */
    padding: 0.5rem 6px;
  `,
  // Docs / Data / Nucleus are pinned to the bottom and never scroll out of
  // view (flex-shrink: 0). The scroll lives in primarySection above.
  secondarySection: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.125rem;
    flex-shrink: 0;
    /* Match the primary section's horizontal inset so both groups' tiles share
       the same near-full width. */
    padding: 0.5rem 6px 0.75rem;
    border-top: 1px solid ${colors.border};
    width: 100%;
  `,
  primaryBtn: (active: boolean) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.2rem;
    width: 100%;
    height: 56px;
    flex-shrink: 0;
    border-radius: 14px;
    cursor: pointer;
    border: none;
    outline: none;
    background-color: ${active ? "rgba(37, 99, 235, 0.1)" : "transparent"};
    color: ${active ? BRAND_COLOR : colors.gray400};
    transition: all 0.18s ease;
    position: relative;

    &:hover {
      background-color: ${active ? "rgba(37, 99, 235, 0.1)" : colors.gray100};
      color: ${active ? BRAND_COLOR : colors.gray600};
    }

    &:active {
      transform: scale(0.94);
    }
  `,
  // Locked-out tile — used for both primary (Fill + Kit / Blend / Materials) and
  // secondary (Docs / Nucleus) items that aren't available yet. Keeps the tile
  // shape so the layout doesn't jump, but mutes the colors, kills hover/active
  // feedback, swaps to a not-allowed cursor, and reserves room for the small
  // Lock badge on the icon.
  lockedBtn: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.2rem;
    width: 100%;
    height: 56px;
    flex-shrink: 0;
    border-radius: 14px;
    cursor: not-allowed;
    border: none;
    outline: none;
    background-color: transparent;
    color: ${colors.gray400};
    opacity: 0.7;
    position: relative;
    transition: opacity 0.18s ease;

    &:hover { opacity: 0.85; }
    &:focus-visible { box-shadow: 0 0 0 2px ${colors.gray200}; }
  `,
  lockBadge: css`
    position: absolute;
    top: -6px;
    right: -6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${colors.gray200};
    color: ${colors.gray600};
  `,
  secondaryBtn: (active: boolean, nucleus = false) => {
    const activeColor = nucleus ? NUCLEUS_COLOR : BRAND_COLOR;
    const activeBg = nucleus ? "rgba(88, 130, 235, 0.08)" : "rgba(37, 99, 235, 0.1)";
    return css`
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.2rem;
      width: 100%;
      height: 56px;
      flex-shrink: 0;
      border-radius: 14px;
      cursor: pointer;
      border: none;
      outline: none;
      background-color: ${active ? activeBg : "transparent"};
      color: ${active ? activeColor : colors.gray400};
      transition: all 0.18s ease;
      position: relative;

      &:hover {
        background-color: ${active ? activeBg : colors.gray100};
        color: ${active ? activeColor : colors.gray600};
      }

      &:active {
        transform: scale(0.94);
      }
    `;
  },
  // position: relative so the lock badge / active dot anchor to the (centered)
  // icon rather than the button box — the box is now near-full-width, so a
  // button-anchored badge would fly out to the far corner.
  primaryIconWrap: css`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 20px;
    position: relative;
  `,
  secondaryIconWrap: css`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 20px;
    position: relative;
  `,
  primaryLabel: css`
    font-size: 10px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.01em;
    white-space: nowrap;
  `,
  secondaryLabel: css`
    font-size: 10px;
    font-weight: 500;
    line-height: 1;
    letter-spacing: 0.01em;
    white-space: nowrap;
  `,
  // Small, muted section headers ("Engine" / "Tools"). Uppercase micro-caps
  // that name each nav group without competing with the tiles. flex-shrink: 0 so
  // they never collapse when the primary section scrolls.
  sectionLabel: css`
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: ${colors.gray400};
    line-height: 1;
    user-select: none;
    flex-shrink: 0;
  `,
  // "Engine" sits above the (scrollable) primary section as a fixed nav sibling.
  engineLabel: css`
    padding: 0.5rem 0 0.25rem;
  `,
  // "Tools" is the first child of the secondary section, just under its
  // top border (the section already supplies the padding above it).
  toolsLabel: css`
    padding-bottom: 0.375rem;
  `,
  activeDot: (nucleus: boolean) => css`
    position: absolute;
    top: -3px;
    right: -5px;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background-color: ${nucleus ? NUCLEUS_COLOR : BRAND_COLOR};
  `,
};

interface SidebarProps {
  isNucleusDockOpen?: boolean;
  onNucleusToggle?: () => void;
}

export const SIDEBAR_WIDTH = SIDEBAR_W;

export const Sidebar: FC<SidebarProps> = ({
  isNucleusDockOpen = false,
  onNucleusToggle,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const renderItem = (item: NavItem) => {
    const isNucleusDockLink = item.isNucleus && pathname !== item.href;
    const isLocked = Boolean(item.locked);
    const isDisabled = Boolean(item.disabled);
    // Neither locked nor disabled items are ever navigable, so they never read as active.
    const pathMatch =
      pathname === item.href || pathname.startsWith(item.href + "/");
    const isActive =
      !isLocked &&
      !isDisabled &&
      (pathMatch || (Boolean(item.isNucleus) && !pathMatch && isNucleusDockOpen));
    const Icon = item.icon;
    const isPrimary = item.primary;
    const isInert = isLocked || isDisabled;

    // Pick the button style: locked > primary > secondary. Locked never
    // shows as "active" since the route isn't reachable. A `disabled` item keeps
    // its normal style but is dimmed and non-clickable (see the css array below).
    const btnCss = isLocked
      ? s.lockedBtn
      : isPrimary
        ? s.primaryBtn(isActive)
        : s.secondaryBtn(isActive, item.isNucleus);

    return (
      <button
        key={item.href}
        disabled={isInert}
        aria-disabled={isInert}
        onClick={(e) => {
          if (isInert) return;
          // Buttons don't get the browser's native anchor behavior, so honor
          // cmd/ctrl+click (new tab) manually. The dock toggle is exempt — it
          // isn't a navigation.
          if ((e.metaKey || e.ctrlKey) && !isNucleusDockLink) {
            window.open(item.href, "_blank", "noopener");
            return;
          }
          if (isNucleusDockLink) {
            onNucleusToggle?.();
            return;
          }
          // Re-clicking the tab you're already on resets it to the tab's home.
          // The reporting embed freezes its report at mount (see ReportingView),
          // so a bare navigate() back to /demand wouldn't reload it after you'd
          // wandered to another report in-frame. The nonce forces ReportingView
          // to remount the embed; non-reporting views simply ignore it.
          navigate(
            item.href,
            pathMatch ? { state: { resetEmbed: Date.now() } } : undefined,
          );
        }}
        onAuxClick={(e) => {
          // Middle-click opens a new tab, matching anchor behavior.
          if (isInert || isNucleusDockLink || e.button !== 1) return;
          window.open(item.href, "_blank", "noopener");
        }}
        css={[btnCss, isDisabled && !isLocked ? css`opacity: 0.45;` : undefined]}
        title={isLocked ? `${item.name} — coming soon` : item.name}
      >
        {/* Badges live inside the icon wrap so they anchor to the centered
            icon, not the near-full-width button box. */}
        <span css={isPrimary ? s.primaryIconWrap : s.secondaryIconWrap}>
          <Icon
            size={22}
            strokeWidth={isActive && !isLocked ? 2 : 1.5}
          />
          {isLocked && (
            <span css={s.lockBadge} aria-hidden="true">
              <Lock size={9} strokeWidth={2.25} />
            </span>
          )}
          {isActive && !isLocked && (
            <span css={s.activeDot(Boolean(item.isNucleus))} />
          )}
        </span>
        <span css={isPrimary ? s.primaryLabel : s.secondaryLabel}>{item.name}</span>
      </button>
    );
  };

  return (
    <aside css={s.container}>
      <div css={s.logoBar}>
        <img src="/images/lmnt-logo.png" alt="Lmnt" css={s.logoImg} />
      </div>

      <nav css={s.nav}>
        <span css={[s.sectionLabel, s.engineLabel]}>Engine</span>
        <div css={s.primarySection}>
          {primaryItems.map(renderItem)}
        </div>
        <div css={s.secondarySection}>
          <span css={[s.sectionLabel, s.toolsLabel]}>Tools</span>
          {secondaryItems.map(renderItem)}
        </div>
      </nav>
    </aside>
  );
};
